import React, { useEffect, useState, useRef, useCallback } from 'react';
import { fetchAlbums, getCoverImageUrl, deleteAlbum } from '../dataService.js';
import { Link } from 'react-router-dom';
import { hasPermission, Permissions } from '../services/authService';
import { 
    Box, 
    TextField, 
    Select, 
    MenuItem, 
    Card, 
    CardMedia, 
    CardContent, 
    Typography, 
    Grid,
    CircularProgress,
    Container,
    InputLabel,
    FormControl,
    IconButton,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button
} from '@mui/material';
import { MoreVert as MoreVertIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

const itemsPerPage = 10;

const Gallery = ({ searchTerm, filter, user, userRole }) => {
  const [albums, setAlbums] = useState([]);
  const [displayedAlbums, setDisplayedAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingImages, setLoadingImages] = useState({});
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSummary, setEditSummary] = useState('');

  const canEdit = hasPermission(userRole, Permissions.EDIT_ALBUM);
  const canDelete = hasPermission(userRole, Permissions.DELETE_ALBUM);

  // Create ref for the observer
  const observer = useRef();
  // Create ref for the last album element
  const lastAlbumElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreAlbums();
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);

  // Initial load
  useEffect(() => {
    const loadAlbums = async () => {
      const albumsData = await fetchAlbums();
      const allAlbums = Object.keys(albumsData)
        .map(key => ({
          id: key,
          ...albumsData[key]
        }))
        .sort((a, b) => b.creation_date - a.creation_date);
      
      setAlbums(allAlbums);
      await loadFilteredBatch(allAlbums, 0, itemsPerPage);
      setLoading(false);
    };
    loadAlbums();
  }, []);

  const preloadImage = (url, albumId) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        setLoadingImages(prev => ({
          ...prev,
          [albumId]: true
        }));
        resolve(url);
      };
      img.onerror = () => {
        setLoadingImages(prev => ({
          ...prev,
          [albumId]: true
        }));
        resolve(url);
      };
      img.src = url;
    });
  };

  const loadFilteredBatch = async (filteredAlbums, start, limit) => {
    const batch = filteredAlbums.slice(start, start + limit);
    if (batch.length === 0) {
      setHasMore(false);
      return;
    }

    // Get URLs first
    const albumsWithUrls = await Promise.all(
      batch.map(async album => {
        const coverImageUrl = await getCoverImageUrl(album.cover_image);
        return {
          ...album,
          coverImageUrl
        };
      })
    );

    // Set albums immediately
    setDisplayedAlbums(start === 0 ? albumsWithUrls : prev => [...prev, ...albumsWithUrls]);
    setHasMore(start + limit < filteredAlbums.length);

    // Preload images in background
    albumsWithUrls.forEach(album => {
      preloadImage(album.coverImageUrl, album.id);
    });
  };

  const loadMoreAlbums = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    
    const start = currentPage * itemsPerPage;
    let filteredAlbums = albums.filter(album =>
      album.title.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort((a, b) => {
      if (filter === 'newest') return b.creation_date - a.creation_date;
      if (filter === 'oldest') return a.creation_date - b.creation_date;
      if (filter === 'alphabetical') return a.title.localeCompare(b.title);
      return 0;
    });

    await loadFilteredBatch(filteredAlbums, start, itemsPerPage);
    setCurrentPage(prev => prev + 1);
    setLoadingMore(false);
  };

  // Reset when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
    setDisplayedAlbums([]);
    
    if (albums.length > 0) {
      const filteredAlbums = albums.filter(album =>
        album.title.toLowerCase().includes(searchTerm.toLowerCase())
      ).sort((a, b) => {
        if (filter === 'newest') return b.creation_date - a.creation_date;
        if (filter === 'oldest') return a.creation_date - b.creation_date;
        if (filter === 'alphabetical') return a.title.localeCompare(b.title);
        return 0;
      });

      loadFilteredBatch(filteredAlbums, 0, itemsPerPage);
    }
  }, [searchTerm, filter]);

  const handleMenuOpen = (event, album) => {
    setMenuAnchor(event.currentTarget);
    setSelectedAlbum(album);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedAlbum(null);
  };

  const handleEditClick = () => {
    setEditTitle(selectedAlbum.title);
    setEditSummary(selectedAlbum.summary);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleEditSave = async () => {
    try {
      await updateAlbum(selectedAlbum.id, {
        title: editTitle,
        summary: editSummary
      }, userRole, user?.uid);

      setDisplayedAlbums(prev => prev.map(album => 
        album.id === selectedAlbum.id 
          ? { ...album, title: editTitle, summary: editSummary }
          : album
      ));

      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating album:', error);
    }
  };

  const handleDeleteClick = async () => {
    if (window.confirm('Are you sure you want to delete this album?')) {
      try {
        await deleteAlbum(selectedAlbum.id, userRole, user?.uid);
        setDisplayedAlbums(prev => prev.filter(album => album.id !== selectedAlbum.id));
        handleMenuClose();
      } catch (error) {
        console.error('Error deleting album:', error);
      }
    }
  };

  return (
    <Box>
      {loading ? (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}>
          <CircularProgress />
        </Box>
      ) : (
        <Container maxWidth="xl" sx={{ pt: 10, pb: 4 }}>
          <Grid container spacing={3}>
            {displayedAlbums.map((album, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={album.id}>
                <Card
                  component={Link}
                  to={`/album/${album.id}`}
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    textDecoration: 'none',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  ref={index === displayedAlbums.length - 1 ? lastAlbumElementRef : null}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={album.coverImageUrl}
                    alt={album.title}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography variant="h6" noWrap>
                      {album.title}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {loadingMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </Container>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {canEdit && (
          <MenuItem onClick={handleEditClick}>
            <EditIcon sx={{ mr: 1 }} /> Edit Album
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDeleteClick}>
            <DeleteIcon sx={{ mr: 1 }} /> Delete Album
          </MenuItem>
        )}
      </Menu>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Album</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Summary"
            value={editSummary}
            onChange={(e) => setEditSummary(e.target.value)}
            margin="normal"
            multiline
            rows={4}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} color="primary">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Gallery;